import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, EventEmitter, Output } from '@angular/core';
import { faLaptop } from '@fortawesome/free-solid-svg-icons';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ciTypesInterface } from 'src/app/util/interfaces/ciTypes';
import { environment } from 'src/environments/environment';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { basicCiInterface, ciInterface, ciInterface2 } from 'src/app/util/interfaces/ci';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { PermissionService } from 'src/app/util/services/permission/permission.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { mspFileInterface } from 'src/app/util/interfaces/mspFile';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { StatusInterfaceCMDB } from 'src/app/util/interfaces/statusInterface';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';
import { ShkDate } from 'shk-date';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';

@Injectable({
	providedIn: 'root',
})
export class CmdbRegisterPageService {
	@Output() divOpened = new EventEmitter<void>();
	@Output() divOpenedStock = new EventEmitter<void>();

	private postNewCiUrl = environment.baseApiUrl + 'cmdb';

	private putCiUrl = environment.baseApiUrl + 'cmdb/indicator/{idCi}';
	private deleteCiUrl = environment.baseApiUrl + 'cmdb/indicator/{idCi}';

	private getAvailableCiTypesUrl = environment.baseApiUrl + 'cmdb/citype';
	private getCiTypeFormUrl = environment.baseApiUrl + 'cmdb/citype/{idCiType}';
	private getCiListUrl = environment.baseApiUrl + 'cmdb/search?{queryString}';
	private getCiBaseLineurl = environment.baseApiUrl + 'cmdb/baseline/{idCi}?date={dateBaseline}';

	private getCiChildrenUrl = environment.baseApiUrl + 'cmdb/relations/{idCi}';
	private postBondCiUrl = environment.baseApiUrl + 'cmdb/relations/{idCi}';
	private deleteBondCiUrl = environment.baseApiUrl + 'cmdb/relations/{idCi}/{idRelation}';
	private deleteMultipleBondCiUrl = environment.baseApiUrl + 'cmdb/multipleRelations/{idCi}';

	private getSubstatusUrl = environment.baseApiUrl + 'cmdb/status/{idStatus}';
	private getSubtypeUrl = environment.baseApiUrl + 'cmdb/subtype/{idType}';
	private getSubmodelsUrl = environment.baseApiUrl + 'cmdb/model/{idModel}';

	private getExistingWorkstationOnAd = environment.baseApiUrl + 'cmdb/checkItemAd';
	private getManageFieldStateByStatusUrl = environment.baseApiUrl + 'cmdb/targetField';

	private basicCiInformationUrl = environment.baseApiUrl + 'cmdbrp/{idCiList}';

	private openModalViewDigitalTermUrl = environment.baseApiUrl + 'cmdbDigitalTerm/openModalViewDigitalTerm';
	private readBaselineUrl = environment.baseApiUrl + 'cmdbDigitalTerm/baseline/{idCMDB}';

	private getUserCopyFromMail = environment.baseApiUrl + 'cmdbDigitalTerm/sendEmailCopy';

	private getInconsistenciesByCmdbIdUrl = environment.baseApiUrl + 'inconsistency/cmdb/{idCMDB}';

	public defaultFieldsLength = 0;
	public ciFieldsLength = 0;
	public customFieldsLength = 0;
	public existingInAd = false;
	public editonly = false;
	public ciType: string;
	public customer: string;

	labels: LabelsInterface = {
		lbl_tip_is_mold: '',
		lbl_novos_modelos_disponibilizados: '',
		lbl_alguns_substatus_disponibilizados: '',
		lbl_general_select_user: 'Select a user',
		lbl_geral_exist_in_ad: 'Exists in AD',
		lbl_geral_not_exist_in_ad: 'Does not exist in AD',
	};

	ONSFields = ['substatus'];

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService,
		private ProfileService: ProfileService,
		private PermissionService: PermissionService,
		private NotificationService: NotificationService,
		private LabelsService: LabelsService
	) {
		this.LabelsService.getLabels().subscribe((labels) => {
			this.labels = labels;
		});
	}

	getBasicCiInformation(idCiList: string): Observable<basicCiInterface[]> {
		const basicCiInformationUrl = this.basicCiInformationUrl.replace('{idCiList}', idCiList);
		return this.http.get<basicCiInterface[]>(basicCiInformationUrl, { headers: this.headers });
	}

	getAvailableCiTypes(): Observable<ciTypesInterface[]> {
		return this.http.get<JSON[]>(this.getAvailableCiTypesUrl, { headers: this.headers }).pipe(
			map((ciTypesRes) => {
				const newCiTypesArray: ciTypesInterface[] = [];
				ciTypesRes.forEach((ciType: any) => {
					const newCiType: ciTypesInterface = {
						id: ciType.id,
						label: ciType.label,
						type: ciType.type,
						icon: ciType.icon ? ciType.icon : faLaptop,
						digitalTerm: ciType.digital_term,
					};

					newCiTypesArray.push(newCiType);
				});

				return newCiTypesArray;
			})
		);
	}

	formatCmdbResToInterface(ciRes: any, childrenRes: ciInterface2['relationships']): ciInterface2 {
		const newCi: ciInterface2 = {
			id: ciRes.cmdb.id,
			type: ciRes.cmdb.type,
			identification: ciRes.cmdb.identification,
			last_audit: ciRes.cmdb.last_audit,
			active: ciRes.cmdb.active,
			location: ciRes.cmdb.location,
			id_location: ciRes.cmdb.location,
			notes: ciRes.cmdb.notes,
			status: ciRes.cmdb.status,
			monthly_depreciation: ciRes.cmdb.monthly_depreciation,
			ready_to_audit: ciRes.cmdb.ready_to_audit,
			cc_customer: ciRes.cmdb.cc_customer,
			id_customer: ciRes.cmdb.customer,
			project: ciRes.cmdb.project,
			price: ciRes.cmdb.price,
			invoice_number: ciRes.cmdb.invoice_number,
			enduser: ciRes.cmdb.enduser,
			id_enduser: ciRes.cmdb.enduser,
			warranty_type: ciRes.cmdb.warranty_type,
			impact: ciRes.cmdb.impact,
			id_impact: ciRes.cmdb.impact,
			next_audit: ciRes.cmdb.next_audit,
			id_department: ciRes.cmdb.department,
			department: ciRes.cmdb.department,
			purchase_order: ciRes.cmdb.purchase_order,
			depreciation_limit: ciRes.cmdb.depreciation_limit,
			physical_location: ciRes.cmdb.physical_location,
			purchase_date: ciRes.cmdb.purchase_date,
			warranty_limit: ciRes.cmdb.warranty_limit,
			relationships: childrenRes,
			cost_center: ciRes.cmdb.cost_center,
			deactivation_date: ciRes.cmdb.deactivation_date,
			label_type: '',
			stock_quantity: 0,
		};

		if (ciRes.customFields) {
			ciRes.customFields.forEach((ciCustomItem: any) => {
				if (!newCi[ciCustomItem] || newCi[ciCustomItem].toString().trim() == '') {
					newCi[ciCustomItem.field] = ciCustomItem.content;
				}
			});
		}
		if (ciRes.ci) {
			for (const ciCustomItem of Object.keys(ciRes.ci)) {
				if (!newCi[ciCustomItem] || newCi[ciCustomItem].toString().trim() == '') {
					newCi[ciCustomItem] = ciRes.ci[ciCustomItem];
				}
			}
		}
		if (ciRes.cmdb) {
			for (const cmdbItem of Object.keys(ciRes.cmdb)) {
				if (!newCi[cmdbItem] || newCi[cmdbItem].toString().trim() == '') {
					newCi[cmdbItem] = ciRes.cmdb[cmdbItem];
				}
			}
		}

		if (ciRes.solutionAreas) {
			newCi['solutionAreas'] = [];
			ciRes.solutionAreas.forEach((solutionArea: any) => {
				newCi['solutionAreas'].push(solutionArea.id);
			});
		}

		return newCi;
	}

	getCiData(idCi: string, date: ShkDate): Observable<ciInterface2> {
		let getCiBaseLineurl = this.getCiBaseLineurl.replace('{idCi}', idCi).replace('{dateBaseline}', date.dateTime);
		getCiBaseLineurl += `&isAnalystView=${localStorage.getItem('sessionStatus') === '1'}`;

		const getCiChildrenUrl = this.getCiChildrenUrl.replace('{idCi}', idCi);

		return forkJoin([
			this.http.get<ciInterface2['relationships']>(getCiChildrenUrl, { headers: this.headers }),
			this.http.get<any>(getCiBaseLineurl, { headers: this.headers }),
		]).pipe(
			map((forkRes) => {
				const childrenRes = forkRes[0];
				const ciRes = forkRes[1];

				return this.formatCmdbResToInterface(ciRes, childrenRes);
			})
		);
	}

	getRegisterForm(idType: string, idCmdb: string = ''): Observable<genericFormI[]> {
		let oldIdType = '';
		if (idType === 'multiple') {
			oldIdType = 'multiple';
			idType = '1';
		}

		const defaultCustomer = this.ProfileService.userData().idCustomer;
		const getCiTypeFormUrl = this.getCiTypeFormUrl.replace('{idCiType}', idType);

		return this.http
			.get<JSON[]>(getCiTypeFormUrl, { headers: this.headers, params: { customer_id: defaultCustomer, idCmdb: idCmdb } })
			.pipe(
				map((ciFormRes: any) => {
					const newFormArray: genericFormI[] = [];
					this.defaultFieldsLength = ciFormRes.cmdb.length;
					this.ciFieldsLength = ciFormRes.ci.length;
					this.customFieldsLength = ciFormRes.customFields.length;

					if (ciFormRes.cmdb) {
						ciFormRes.cmdb.forEach((formItem: any) => {
							//Lógica implementada para ocultar campos que somente a ONS utiliza
							if (this.ONSFields.includes(formItem.name) && this.ProfileService.userData().customer != 'ONS') {
								formItem.hidden = true;
							}

							const newForm: genericFormI = {
								id: formItem.id,
								size: formItem.size,
								items: [],
								label: formItem.label,
								name: formItem.name,
								tip: formItem.tip,
								required: formItem.required === 1 || formItem.required === true,
								rawRequired: formItem.required === 1 || formItem.required === true,
								hidden: formItem.hidden,
								rawHidden: formItem.hidden,
								disabled: formItem.disabled,
								rawDisabled: formItem.disabled,
								values: formItem.values !== null && formItem.values.length > 0 ? formItem.values : [],
								inputType: formItem.inputType,
								qryInputType: formItem.qryInputType,
								commonGroup: 'CMDB',
								autoWriteScript: formItem.autoWriteScript,
								subSelect: [],
								extraFunction: null,
								filterItem: [],
								filterValues: [],
								rawValues: formItem.rawValues ? formItem.rawValues : [''],
								outItems: [],
							};

							if (formItem.subSelect && formItem.subSelect.length > 0) {
								for (const subSelect of formItem.subSelect) {
									const newSubSelect: genericFormI['subSelect'][number] = {
										subSelectChildName: subSelect.subSelectChildName,
										subSelectQryName: subSelect.subSelectQryName,
										when_value_is: subSelect.when_value_is,
										make_required: subSelect.make_required,
										parameters: subSelect.field_as_parameters,
									};

									newForm.subSelect.push(newSubSelect);
								}
							}

							if (formItem.items && formItem.items.length > 0) {
								for (const item of formItem.items) {
									const newItem: genericFormI['items'][number] = {
										label: item.label,
										name: item.name,
										other_names: item.other_names,
										values: item.value == undefined ? item.values : [item.value],
										rawValues: item.rawValues == undefined ? item.rawValues : [item.rawValues],
										inputType: item.inputType,
										items: item.items,
										hidden: item.hidden,
										required: item.required,
										disabled: item.disabled,
									};

									newForm.items.push(newItem);
								}
							}

							newFormArray.push(newForm);
						});
					}

					//Empty CI creation case, just necessary the base CMDB form.
					if (oldIdType && oldIdType === 'multiple') {
						this.mockMapper(newFormArray, idType);
						this.ciFieldsLength = 0;
						this.customFieldsLength = 0;
						return newFormArray;
					}
					//EO Empty ci creation case

					if (ciFormRes.ci) {
						ciFormRes.ci.forEach((formItem: any) => {
							const newForm: genericFormI = {
								id: formItem.id,
								size: formItem.size,
								items: [],
								label: formItem.label,
								name: formItem.name,
								tip: formItem.tip,
								required: formItem.required === 1 || formItem.required === true,
								rawRequired: formItem.required === 1 || formItem.required === true,
								hidden: formItem.hidden,
								rawHidden: formItem.hidden,
								disabled: formItem.disabled,
								rawDisabled: formItem.disabled,
								values: formItem.values !== null && formItem.values.length > 0 ? formItem.values : [],
								inputType: formItem.inputType,
								commonGroup: 'ci',
								autoWriteScript: formItem.autoWriteScript,
								subSelect: [],
								filterItem: [],
								filterValues: [],
								rawValues: formItem.rawValues ? formItem.rawValues : [''],
								outItems: [],
							};

							if (formItem.subSelect && formItem.subSelect.length > 0) {
								for (const subSelect of formItem.subSelect) {
									const newSubSelect: genericFormI['subSelect'][number] = {
										subSelectChildName: subSelect.subSelectChildName,
										subSelectQryName: subSelect.subSelectQryName,
										when_value_is: subSelect.when_value_is,
										make_required: subSelect.make_required,
										parameters: subSelect.field_as_parameters,
									};

									newForm.subSelect.push(newSubSelect);
								}
							}

							if (formItem.items && formItem.items.length > 0) {
								for (const item of formItem.items) {
									const newItem: genericFormI['items'][number] = {
										label: item.label,
										name: item.name,
										values: item.value == undefined ? item.values : [item.value],
										rawValues: item.rawValues == undefined ? item.rawValues : [item.rawValues],
										inputType: item.inputType,
										items: item.items,
										hidden: item.hidden,
										required: item.required,
										disabled: item.disabled,
									};

									newForm.items.push(newItem);
								}
							}

							newFormArray.push(newForm);
						});
					}

					if (ciFormRes.customFields) {
						ciFormRes.customFields.forEach((formItem: any) => {
							const newForm: genericFormI = {
								id: formItem.id,
								size: formItem.size,
								items: [],
								label: formItem.label,
								name: formItem.name,
								tip: formItem.tip,
								required: formItem.required === 1 || formItem.required === true,
								rawRequired: formItem.required === 1 || formItem.required === true,
								hidden: formItem.hidden,
								rawHidden: formItem.hidden,
								disabled: formItem.disabled,
								rawDisabled: formItem.disabled,
								values: formItem.values !== null && formItem.values.length > 0 ? formItem.values : [],
								inputType: formItem.inputType,
								commonGroup: 'customfields',
								autoWriteScript: formItem.autoWriteScript,
								subSelect: [],
								filterItem: [],
								filterValues: [],
								rawValues: formItem.rawValues ? formItem.rawValues : [''],
								outItems: [],
							};

							if (formItem.subSelect && formItem.subSelect.length > 0) {
								for (const subSelect of formItem.subSelect) {
									const newSubSelect: genericFormI['subSelect'][number] = {
										subSelectChildName: subSelect.subSelectChildName,
										subSelectQryName: subSelect.subSelectQryName,
										when_value_is: subSelect.when_value_is,
										make_required: subSelect.make_required,
										parameters: subSelect.field_as_parameters,
									};

									newForm.subSelect.push(newSubSelect);
								}
							}

							if (formItem.items && formItem.items.length > 0) {
								for (const item of formItem.items) {
									const newItem: genericFormI['items'][number] = {
										label: item.label,
										name: item.name,
										values: item.value == undefined ? item.values : [item.value],
										rawValues: item.rawValues == undefined ? item.rawValues : [item.rawValues],
										inputType: item.inputType,
										items: item.items,
										value: item.value,
										hidden: item.hidden,
										required: item.required,
										disabled: item.disabled,
									};

									newForm.items.push(newItem);
								}
							}

							newFormArray.push(newForm);
						});
					}
					if (ciFormRes.solutionAreas) {
						const newForm: genericFormI = {
							id: ciFormRes.solutionAreas.id,
							inputType: ciFormRes.solutionAreas.inputType,
							label: ciFormRes.solutionAreas.label,
							name: ciFormRes.solutionAreas.name,
							required: ciFormRes.solutionAreas.required === 1 || ciFormRes.solutionAreas.required === true,
							values: [],
							items: [],
							commonGroup: 'solutionArea',
							filterItem: [],
							filterValues: [],
							rawValues: ciFormRes.rawValues ? ciFormRes.rawValues : [''],
							outItems: [],
						};

						if (!this.PermissionService.getPermission('CMDB_bind_solution_area')) {
							newForm.required = false;
						}

						if (ciFormRes.solutionAreas.items && ciFormRes.solutionAreas.items.length > 0) {
							for (const item of ciFormRes.solutionAreas.items) {
								const newItem: genericFormI['items'][number] = {
									label: item.label,
									name: item.name,
									values: item.value == undefined ? item.values : [item.value],
									rawValues: item.rawValues == undefined ? item.rawValues : [item.rawValues],
									inputType: item.inputType,
									items: item.items,
									disabled: item.disabled,
								};

								newForm.items.push(newItem);
							}
						}

						newFormArray.push(newForm);
					}

					this.mockMapper(newFormArray, idType);

					return newFormArray;
				})
			);
	}

	createCiWithGeneric(formCi: genericFormI[], quantity: number, createLog = ''): Observable<any> {
		let formCiCopy = $.extend(true, [], formCi);

		formCiCopy = this.validateValuesGenericForm(formCiCopy);
		const body = this.getGenericFormChangeBody(formCiCopy);
		body.ci['cmdb_id'] = 0;
		return this.http
			.post<ciInterface2>(this.postNewCiUrl, body, { headers: this.headers, params: { quantity: quantity, createLog: createLog } })
			.pipe(
				map((ciRes) => {
					return quantity == 1 ? ciRes : ciRes;
				})
			);
	}

	createCiWithBody(body: any, createLog = ''): Observable<any> {
		body.ci['cmdb_id'] = 0;
		return this.http.post<ciInterface2>(this.postNewCiUrl, body, {
			headers: this.headers,
			params: { quantity: 1, createLog: createLog },
		});
	}

	updateCiWithGeneric(formCi: genericFormI[], idCi: string, updateLog = ''): Observable<JSON> {
		let formCiCopy = $.extend(true, [], formCi);
		const putCiUrl = this.putCiUrl.replace('{idCi}', idCi);

		formCiCopy = this.validateValuesGenericForm(formCiCopy);
		const body = this.getGenericFormChangeBody(formCiCopy);
		return this.http.put<JSON>(putCiUrl, body, { headers: this.headers, params: { updateLog: updateLog } });
	}

	updateCiWithBody(body: any, idCi: string, updateLog = ''): Observable<JSON> {
		const putCiUrl = this.putCiUrl.replace('{idCi}', idCi);
		return this.http.put<JSON>(putCiUrl, body, { headers: this.headers, params: { updateLog: updateLog } });
	}

	deactivateCi(idCi: string, typeCi: string): Observable<JSON> {
		const deleteCiUrl = this.deleteCiUrl.replace('{idCi}', idCi);
		return this.http.delete<JSON>(deleteCiUrl, { headers: this.headers });
	}

	formatDateToService(date: Date): string {
		date = new Date(date);
		return `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1}-${date.getUTCDate() < 10 ? '0' + date.getUTCDate() : date.getUTCDate()}`;
	}

	validateValuesGenericForm(genericForm: genericFormI[]): genericFormI[] {
		for (const form of genericForm) {
			if (
				(form.inputType === 'boolean' || form.inputType === 'select-iac') &&
				(!form.values[0] || form.values[0] === '' || form.values[0] === -1 || form.values[0] === '-1')
			) {
				form.values[0] = 0;
			}
			if (
				form.inputType === 'select' &&
				(!form.values[0] || form.values[0] === '' || form.values[0] === -1 || form.values[0] === '-1')
			) {
				form.values[0] = form.items[0] && isNaN(form.items[0].values[0]) ? '' : 0;
			}
			if (form.inputType === 'multi-select' || form.inputType === 'checkbox') {
				const valuesOnly = [];
				for (const itemAsValue of form.values) {
					valuesOnly.push(itemAsValue.values[0]);
				}
				form.values = valuesOnly;
			}
		}

		return genericForm;
	}

	getGenericFormChangeBody(genericForm: genericFormI[]): { file_attach: string; ci: {}; customFields: {}; cmdb: {} } {
		const body = {
			file_attach: '',
			ci: {},
			customFields: {},
			cmdb: {},
			solutionAreas: [],
		};

		for (const [index, formItem] of genericForm.entries()) {
			let temporaryValue = undefined;
			if (
				formItem.inputType == 'number' ||
				formItem.inputType == 'money' ||
				formItem.inputType == 'boolean' ||
				formItem.inputType == 'select' ||
				(formItem.inputType == 'select-iac' && formItem.qryInputType == 'int')
			) {
				temporaryValue = formItem.values[0] == undefined || formItem.values[0] == '' ? 0 : formItem.values[0];
			}
			if (formItem.inputType == 'multi-input') {
				temporaryValue = [];
				for (let index_ = 0; index_ < formItem.values.length; index_++) {
					temporaryValue.push(formItem.values[index_].join('§'));
				}
				temporaryValue = JSON.stringify(temporaryValue);
			}
			if (temporaryValue == undefined) {
				if (formItem.values[0] == undefined) temporaryValue = '';
				else if (formItem.values.length > 1) temporaryValue = formItem.values;
				else temporaryValue = formItem.values[0];
			}

			if (index < this.defaultFieldsLength) body.cmdb[formItem.name] = temporaryValue;
			else if (index < this.defaultFieldsLength + this.ciFieldsLength) body.ci[formItem.name] = temporaryValue;
			else if (index < this.defaultFieldsLength + this.ciFieldsLength + this.customFieldsLength)
				body.customFields[formItem.name] = temporaryValue;
			else body.solutionAreas = formItem.values;
		}

		delete body.cmdb['digital_term_status'];

		return body;
	}

	mockMapper(genericForm: genericFormI[], idType: string): genericFormI[] {
		let notesItem: genericFormI<'textarea'>;
		const idCustomer = this.ProfileService.userData().idCustomer;

		for (let index = 0; index < genericForm.length; index++) {
			const formItem = genericForm[index];

			if (formItem.name === 'notes') {
				notesItem = formItem;
				genericForm.splice(index, 1);
				index--;
			}
			if (formItem.name === 'enduser' || formItem.name.trim() === 'enduser' || formItem.name.toLowerCase() === 'enduser') {
				formItem.tipFunction = () => {
					this.divOpened.emit();
				};
			}
			// if(formItem.name === 'status' || formItem.name.trim() === 'status' || formItem.name.toLowerCase() === 'status') { formItem.tipFunction = () => { this.divOpenedStock.emit(); }}
			if (formItem.name === 'customer') {
				formItem.disabled = true;
				formItem.values[0] = idCustomer;
			}
			if (formItem.name === 'type') {
				formItem.disabled = true;
				formItem.items[0] = { label: idType, name: idType, values: [idType] };
				formItem.values[0] = idType;
			}
			if (formItem.name === 'disuse_period' && !this.editonly) {
				formItem.values = [90];
			}
			if (formItem.inputType === 'multi-input') {
				formItem.values = [];
			}
		}

		if (notesItem != undefined) {
			notesItem.values = [''];
			genericForm.splice(this.defaultFieldsLength - 2, 0, notesItem);
		}

		return genericForm;
	}

	searchCiList(searchParameters: ciInterface): Observable<ciInterface> {
		let queryString = '';
		for (const key of Object.keys(searchParameters.ciArray[0])) {
			if (searchParameters.ciArray[0][key] !== '' && searchParameters.ciArray[0][key] !== null) {
				queryString += `${key}=${searchParameters.ciArray[0][key]}&`;
			}
		}
		const getCiListUrl = this.getCiListUrl.replace('{queryString}', queryString);
		const newCiList: ciInterface = {
			ciType: 'any',
			ciArray: [],
		};
		if (queryString === '') {
			return of(newCiList);
		}
		return this.http.get(getCiListUrl, { headers: this.headers }).pipe(
			map((ciListRes: ciInterface['ciArray'][number][]) => {
				for (const ci of ciListRes) {
					const newCi: ciInterface['ciArray'][number] = {
						id: ci.id,
						cmdb_id: ci.id,
						identification: ci.identification,
						status: ci.status,
						type: ci.type,
					};

					newCiList.ciArray.push(newCi);
				}

				return newCiList;
			})
		);
	}

	bondCi(ci: ciInterface2, bondCi: ciInterface2, type_bond: string): Observable<any> {
		const postBondCiUrl = this.postBondCiUrl.replace('{idCi}', ci.id);
		const body = {
			bond: [
				{
					relatedCiId: bondCi.id,
					relationType: type_bond,
				},
			],
		};

		return this.http.post<any[]>(postBondCiUrl, body, { headers: this.headers });
	}

	unbondCi(idCi: string, idRelation: string): Observable<any> {
		const deleteBondCiUrl = this.deleteBondCiUrl.replace('{idCi}', idCi).replace('{idRelation}', idRelation);
		return this.http.delete(deleteBondCiUrl, { headers: this.headers });
	}

	unbondCiMultiples(idCi: string, listCiBond): Observable<any> {
		const deleteMultipleBondCiUrl = this.deleteMultipleBondCiUrl.replace('{idCi}', idCi);
		const body = {
			content: listCiBond,
		};
		return this.http.put(deleteMultipleBondCiUrl, body, { headers: this.headers });
	}

	getExistingWorkstationsOnAd(idCi: string) {
		const getExistingWorkstationOnAd = this.getExistingWorkstationOnAd + `?cmdb_id=${idCi}`;
		return this.http.get<JSON[]>(getExistingWorkstationOnAd, { headers: this.headers });
	}

	getManageFieldStateByStatus(status: string, ciType: string): Observable<StatusInterfaceCMDB[]> {
		const getManageFieldStateByStatusUrl = this.getManageFieldStateByStatusUrl + `?status=${status}&type=${ciType}`;
		return this.http.get<StatusInterfaceCMDB[]>(getManageFieldStateByStatusUrl, { headers: this.headers });
	}

	openModalViewDigitalTerm(parameter: any): Observable<boolean> {
		let parameters = new HttpParams();

		for (const key of Object.keys(parameter)) {
			if (parameter[key]) {
				parameters = parameters.append(key, parameter[key]);
			}
		}

		return this.http.get<boolean>(this.openModalViewDigitalTermUrl, { headers: this.headers, params: parameters });
	}

	readBaseline(idCMDB: string): Observable<any> {
		const readBaselineUrl = this.readBaselineUrl.replace('{idCMDB}', idCMDB);

		return this.http.get<any>(readBaselineUrl, { headers: this.headers });
	}

	getSendEmailCopy(): Observable<any> {
		return this.http.get<any>(this.getUserCopyFromMail, { headers: this.headers });
	}

	getInconsistencies(idCMDB: string): Observable<any> {
		const getInconsistenciesUrl = this.getInconsistenciesByCmdbIdUrl.replace('{idCMDB}', idCMDB);
		return this.http.get<any>(getInconsistenciesUrl, { headers: this.headers });
	}
}
